/**
 * Outlook / Engagement component.
 *
 * "Are the people responsible for this project actually engaging with it?"
 *
 * Measured via DigestAcknowledgment activity over a recent window. A project
 * with N RACI assignments should generate ack activity proportional to N.
 * Silence (no acks for 14+ days) is a leading indicator of imminent reporting
 * drift — disengaged owners stop submitting reports.
 *
 * Score band (linear from days-since-last-ack):
 *   0-7 days   → 100 (active engagement)
 *   7-14 days  → 100 → 70 (slipping)
 *   14-30 days → 70 → 30 (concerning)
 *   30+ days   → 30 → 0 (effectively disengaged)
 *
 * Lifecycle dependency: composite-level Outlook gate already requires
 * `raci_assignment_count >= 1`, so this component can assume that at the
 * call site. If somehow no acks have ever occurred AND the project is
 * past the 30-day Outlook age threshold, score is 0 (not N/A) — silence
 * IS the signal.
 *
 * See: DEV-846 epic, DEV-847.
 */

import type { ComponentResult, DataReliabilityInputs } from '../types';

/** Days between two ISO strings. */
function daysBetween(a: string, b: string): number {
  const ma = Date.parse(a);
  const mb = Date.parse(b);
  if (!Number.isFinite(ma) || !Number.isFinite(mb)) return 0;
  return Math.round((mb - ma) / (1000 * 60 * 60 * 24));
}

/**
 * Convert "days since last ack" to a 0-100 score.
 * 0-7 days → 100 (fresh engagement).
 * 7-14   → linear 100 → 70.
 * 14-30  → linear 70 → 30.
 * 30+    → linear 30 → 0 by day 60, then 0.
 */
function scoreFromGapDays(gap: number): number {
  if (gap <= 7) return 100;
  if (gap <= 14) return Math.round(100 - ((gap - 7) / 7) * 30);
  if (gap <= 30) return Math.round(70 - ((gap - 14) / 16) * 40);
  if (gap <= 60) return Math.round(30 - ((gap - 30) / 30) * 30);
  return 0;
}

export function computeEngagement(
  inputs: DataReliabilityInputs,
): ComponentResult {
  if (inputs.project_metadata.raci_assignment_count < 1) {
    // Defensive — composite-level gate should have caught this, but
    // engine remains correct even if called without the gate.
    return {
      key: 'engagement',
      score: null,
      applicable: false,
      reason: 'No RACI assignments — engagement signal unavailable',
      explain: 'No one assigned to acknowledge work.',
    };
  }

  const acks = inputs.digest_acks;

  if (acks.length === 0) {
    // Project has assignments but no acks ever — fully disengaged.
    return {
      key: 'engagement',
      score: 0,
      applicable: true,
      explain: 'No acknowledgment activity recorded.',
    };
  }

  // Most recent ack timestamp.
  let mostRecent = '';
  for (const a of acks) {
    if (a.acknowledged_at > mostRecent) mostRecent = a.acknowledged_at;
  }

  const gap = Math.max(0, daysBetween(mostRecent, inputs.as_of_date));
  const score = scoreFromGapDays(gap);

  let explain: string;
  if (gap <= 7) {
    explain = `Recent ack activity (last ack ${gap}d ago).`;
  } else if (gap <= 14) {
    explain = `Engagement slipping — last ack ${gap}d ago.`;
  } else if (gap <= 30) {
    explain = `Concerning gap — no ack activity for ${gap}d.`;
  } else {
    explain = `Effectively disengaged — no ack activity for ${gap}d.`;
  }

  return {
    key: 'engagement',
    score,
    applicable: true,
    explain,
  };
}
